import React, { useCallback, useMemo, useState } from 'react';
import { Skeleton, Stack } from '@mui/material';
import {
  ComponentFormContainer,
  CustomizeProvider,
  FrontendClientProvider,
  useComponent,
} from '@pipedream/connect-react';
import { createPipedreamConnectClient } from '../../utils/PipeDreamUtil';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import pipedreamCustomiseConfig from '../TriggerFromExternal/PipedreamCustomiseConfig';
import './PipedreamInlineConfigForm.css';

/**
 * Form-shaped placeholder shown while the component definition is being fetched, so the inline config
 * reserves space and reads as "loading" instead of flashing an empty box that suddenly fills in.
 */
const ConfigFormSkeleton = () => (
  <Stack
    spacing={1}
    sx={{
      padding: '0.75rem',
      backgroundColor: 'var(--color-bg-paper)',
      border: '1px solid var(--color-divider)',
      borderRadius: '8px',
    }}
  >
    <Skeleton variant="text" width="40%" height={18} />
    <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: '6px' }} />
    <Skeleton variant="text" width="35%" height={18} />
    <Skeleton variant="rectangular" width="100%" height={28} sx={{ borderRadius: '6px' }} />
  </Stack>
);

/**
 * Inner form body. Lives inside FrontendClientProvider so it can use `useComponent` to gate on the
 * component-definition fetch. This shares connect-react's query cache, so once it resolves the
 * ComponentFormContainer reads the same cached component and renders immediately (no second request,
 * no empty-then-pop-in flash). componentKey is keyed by the parent so a new trigger/action remounts
 * this and re-shows the skeleton while its definition loads.
 */
const PipedreamConfigFormBody = ({ componentKey, userId, configuredProps, onUpdateConfiguredProps }) => {
  const { isLoading, isLoadingError } = useComponent({ key: componentKey });

  if (isLoading) return <ConfigFormSkeleton />;
  if (isLoadingError) return null;

  return (
    <ComponentFormContainer
      userId={userId}
      componentKey={componentKey}
      configuredProps={configuredProps}
      onUpdateConfiguredProps={onUpdateConfiguredProps}
    />
  );
};

/**
 * Renders the Pipedream Connect dynamic-props form (ComponentFormContainer) directly inside an inline
 * node config, so trigger/action properties are configured on the node itself rather than behind the
 * expanded editor. Shared by the trigger and action inline configs.
 *
 * The Pipedream external user id (clientId) defaults to the project store, where it is populated at app
 * init from `authz-service/user-info`. The canvas project always belongs to the current user's org, so
 * this is the same value the dedicated `get_client_by_project` lookup would return — reusing it avoids an
 * extra request (which is also CORS-blocked when running the frontend locally against a remote backend).
 * The full-pane template passes `clientId` explicitly (it resolves it via authz-service), which takes
 * precedence over the store value.
 *
 * The first-update guard mirrors the full-pane template: when the form first loads it can emit an update
 * that only strips optional props, which must not overwrite saved configuredProps (and, inline, must not
 * trigger a spurious auto-save).
 */
const PipedreamInlineConfigForm = ({
  componentKey,
  configurableProps,
  configuredProps,
  onUpdateConfiguredProps,
  clientId: clientIdProp,
}) => {
  // Inline (canvas) usage reads the external user id from the project store; the full-pane template
  // passes it explicitly (it resolves clientId via authz-service), so prefer the prop when given.
  const storeClientId = useProjectStore((s) => s.clientId);
  const resolvedClientId = clientIdProp != null ? clientIdProp : storeClientId;
  const clientId = resolvedClientId != null ? String(resolvedClientId) : '';
  const [isFirstUpdate, setIsFirstUpdate] = useState(true);

  // Build the client only once clientId is known — the Pipedream SDK throws
  // "No external user ID provided" if externalUserId is empty.
  const pipeDreamClient = useMemo(
    () => (clientId ? createPipedreamConnectClient(clientId) : null),
    [clientId]
  );

  const handleUpdate = useCallback(
    (newConfiguredProps) => {
      if (!isFirstUpdate) {
        onUpdateConfiguredProps(newConfiguredProps);
        return;
      }
      const currentKeys = Object.keys(configuredProps || {});
      const newKeys = Object.keys(newConfiguredProps || {});
      const removedKeys = currentKeys.filter((key) => !newKeys.includes(key));
      const optionalPropNames = (configurableProps || [])
        .filter((prop) => prop.optional)
        .map((prop) => prop.name);
      const onlyRemovedOptional =
        removedKeys.length > 0 && removedKeys.every((key) => optionalPropNames.includes(key));

      if (onlyRemovedOptional) {
        setIsFirstUpdate(false);
      } else {
        onUpdateConfiguredProps(newConfiguredProps);
      }
    },
    [isFirstUpdate, configuredProps, configurableProps, onUpdateConfiguredProps]
  );

  if (!componentKey) return null;

  // The store's clientId is populated during app init; until it is, show a loading placeholder rather
  // than nothing (and never mount the Pipedream form without a valid external user id).
  if (!clientId || !pipeDreamClient) {
    return <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: '4px' }} />;
  }

  return (
    <FrontendClientProvider client={pipeDreamClient}>
      <CustomizeProvider {...pipedreamCustomiseConfig}>
        <PipedreamConfigFormBody
          key={componentKey}
          userId={clientId}
          componentKey={componentKey}
          configuredProps={configuredProps}
          onUpdateConfiguredProps={handleUpdate}
        />
      </CustomizeProvider>
    </FrontendClientProvider>
  );
};

export default PipedreamInlineConfigForm;
